import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import Tab from "react-bootstrap/Tab";
import moment from "moment";
import { Link } from "react-router-dom";
import { EncounterHistoryTitle, noDataAvailText } from "../Constants";
import { DateSelectorWithoutFormik } from "../../../common/textfield/DateSelector";
import { usePatientEncounterHistoryQuery } from "../../../../hooks/ReactQueryHooks/usePatientEncounterHistoryQuery";
import CustomPagination from "../../../common/components/Pagination/CustomPagination";
import { getPatientData } from "../../../api/Individual";
import ViewIcon from "../../../../assets/images/view.png";
import { ViewDateTime } from "../../../../utils/DateSupport";
import { useSelector } from "react-redux";

// Default lookback window for the patient dashboard's encounter history list.
const DEFAULT_ENCOUNTER_RANGE_MONTHS = 3;

export function EncounterHistoryList({ patientId }) {
    const [encounterStartDate, setEncounterStartDate] = useState(
        moment().subtract(DEFAULT_ENCOUNTER_RANGE_MONTHS, "months").format("MM-DD-YYYY")
    );
    const [encounterEndDate, setEncounterEndDate] = useState(
        moment().format("MM-DD-YYYY")
    );
    const [encounterData, setEncounterData] = useState([]);
    const [patientData, setPatientData] = useState(null);
    const [page, setPage] = useState(0);
    const rowsPerPage = 5;
    const userRoles = useSelector((state) => state?.auth?.user?.["cognito:groups"]);
    const isSuperAdmin = userRoles?.includes("Super-Admin");
    const patientOrgId = patientData?.managingOrganization?.[0]?.reference?.split("/")?.[1];

    const { isLoading, isFetching } = usePatientEncounterHistoryQuery({
        onSuccess: setEncounterData,
        startDate: encounterStartDate,
        endDate: encounterEndDate,
        patientId,
        orgId: isSuperAdmin ? patientOrgId : undefined,
        enabled: !isSuperAdmin || Boolean(patientOrgId),
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        getPatientData(patientId).then((response) => setPatientData(response?.result));
    }, [patientId]);

    return (
        <Tab.Pane eventKey="encounters">
            <div className="mnth-date-wrap">
                <DateSelectorWithoutFormik
                    label="Start Date"
                    minDate=""
                    maxDate={moment(encounterEndDate)}
                    value={moment(encounterStartDate)}
                    handleChange={(value) => setEncounterStartDate(value.format("MM-DD-YYYY"))}
                    type="readonly"
                />
                <DateSelectorWithoutFormik
                    label="End Date"
                    minDate={moment(encounterStartDate)}
                    maxDate={moment()}
                    value={moment(encounterEndDate)}
                    handleChange={(value) => setEncounterEndDate(value.format("MM-DD-YYYY"))}
                    type="readonly"
                />
            </div>
            <div className="table-wrap">
                <Table responsive>
                    <thead>
                        <tr>
                            {EncounterHistoryTitle.map((title) => <th key={title}>{title}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading || isFetching ? (
                            [1, 2, 3, 4, 5, 6].map((row) => (
                                <tr key={row}>
                                    {EncounterHistoryTitle.map((title) => (
                                        <td key={title}><Skeleton animation="wave" /></td>
                                    ))}
                                </tr>
                            ))
                        ) : encounterData?.length > 0 ? (
                            encounterData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map(({ id, period, participant }) => (
                                    <tr key={id}>
                                        <td>{ViewDateTime(period?.end)}</td>
                                        <td>{id || "-"}</td>
                                        <td>
                                            {participant?.at(-1)?.individual?.display
                                                ?.split(" ")
                                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                                .join(" ") || "-"}
                                        </td>
                                        <td>
                                            <div className="action-wrap" style={{ justifyContent: "start" }}>
                                                <Tooltip title="View">
                                                    <Link
                                                        to="/app/encounter-details"
                                                        state={{ encounterId: id, from: "pastEncounter" }}
                                                        className="view-btn"
                                                    >
                                                        <img src={ViewIcon} alt="View" />
                                                    </Link>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <TableCell colSpan={EncounterHistoryTitle.length} style={{ textAlign: "center" }}>
                                    {noDataAvailText}
                                </TableCell>
                            </tr>
                        )}
                    </tbody>
                </Table>
                {encounterData?.length > 0 && (
                    <CustomPagination
                        tableData={encounterData}
                        totalRows={encounterData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        handlePage={setPage}
                    />
                )}
            </div>
        </Tab.Pane>
    );
}
